import React, { useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ContextMenu, Table, TextWithTooltip } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import { COMPASS_DE_ORGANIZATION_ID } from '@ubeya/shared/utils/accounts';
import { ReactComponent as RecordsEmptyIcon } from '@ubeya/shared-web/assets/calendar.svg';
import { formatBalanceUnitSuffix } from '../leaveBalanceUtils';
import { formatDate, formatMoney, formatNumber } from '../leaveFormatters';
import { MiniEmpty, MiniEmptyHint, MiniEmptyTitle, NumericCell } from './styled';

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;

  ${({ $status, theme }) => {
    if ($status === 'approved')
      return css`
        background: ${theme.colors.greenExtraLight};
        color: ${theme.colors.greenPositive};
      `;
    if ($status === 'pending')
      return css`
        background: ${theme.colors.yellowLight};
        color: ${theme.colors.orangeChanges};
      `;
    if ($status === 'processed')
      return css`
        background: ${theme.colors.blueLight};
        color: ${theme.colors.blue};
      `;
    return css`
      background: ${theme.colors.grey1};
      color: ${theme.colors.grey4};
    `;
  }}
`;

const recordStatusOf = (record) => (record.payrollProcessId ? 'processed' : 'pending');

const RecordStatusPill = ({ record }) => {
  const { t } = useContext(ConfigContext);
  const status = recordStatusOf(record);
  const label =
    status === 'pending' ? t('employeeLeave_history_status_pending') : t('employeeLeave_history_status_processed');
  return (
    <StatusPill $status={status}>
      <TextWithTooltip>{label}</TextWithTooltip>
    </StatusPill>
  );
};

const RecordsTab = ({ records, onCancelRecord }) => {
  const { t, organizationId } = useContext(ConfigContext);
  const isCompassDE = organizationId === COMPASS_DE_ORGANIZATION_ID;
  const formattingMap = useFormattingMap();
  const { mappedAdmins } = useAdmins();

  const columns = useMemo(
    () => [
      {
        Header: t('employeeLeave_history_col_date'),
        dataKey: 'date',
        Cell: ({ rowData }) => <>{formatDate(formattingMap, rowData.date)}</>
      },
      {
        Header: t('employeeLeave_history_col_type'),
        dataKey: 'leaveTypeName',
        Cell: ({ rowData }) => <>{rowData.leaveTypeName}</>
      },
      {
        Header: t('employeeLeave_history_col_change'),
        dataKey: 'durationHours',
        Cell: ({ rowData }) => {
          // Display unit follows leave config — days/money render differently from raw durationHours.
          const hours = Number(rowData.durationHours ?? 0);
          const magnitude = Math.abs(hours);
          if (!magnitude) return <NumericCell>0</NumericCell>;
          const leaveUnit = rowData.leaveUnit || 'hours';
          if (leaveUnit === 'money') {
            const wagePay = Number(rowData.wagePay ?? 0);
            return <NumericCell $tone="negative">{`-${formatMoney(formattingMap, Math.abs(wagePay))}`}</NumericCell>;
          }
          if (leaveUnit === 'days') {
            return (
              <NumericCell $tone="negative">{`-${formatNumber(1)}${formatBalanceUnitSuffix(
                { unit: 'days' },
                t
              )}`}</NumericCell>
            );
          }
          return (
            <NumericCell $tone="negative">{`-${formatNumber(magnitude)}${formatBalanceUnitSuffix(
              { unit: 'hours' },
              t
            )}`}</NumericCell>
          );
        }
      },
      {
        Header: t('employeeLeave_history_col_reason'),
        dataKey: 'reason',
        Cell: ({ rowData: { reason } }) => <>{reason || '-'}</>
      },
      {
        Header: t('employeeLeave_history_col_status'),
        dataKey: 'status',
        Cell: ({ rowData }) => <RecordStatusPill record={rowData} />
      },
      {
        Header: t('approvedBy'),
        dataKey: 'actionBy',
        Cell: ({ rowData }) => {
          const admin = mappedAdmins[rowData.actionBy];
          return <>{admin ? [admin.firstName, admin.lastName].filter(Boolean).join(' ') : '-'}</>;
        }
      },
      {
        Header: '',
        dataKey: 'actions',
        width: 48,
        Cell: ({ rowData }) =>
          recordStatusOf(rowData) === 'pending' || isCompassDE ? (
            <ContextMenu
              hideIconBackground
              usePortal
              openFromRight
              options={[{ label: t('cancel'), onClick: () => onCancelRecord(rowData) }]}
            />
          ) : null
      }
    ],
    [t, formattingMap, mappedAdmins, onCancelRecord, isCompassDE]
  );

  if (!records || records.length === 0) {
    return (
      <MiniEmpty>
        <RecordsEmptyIcon />
        <MiniEmptyTitle>{t('employeeLeave_emptyRecords_title')}</MiniEmptyTitle>
        <MiniEmptyHint>{t('employeeLeave_emptyRecords_hint')}</MiniEmptyHint>
      </MiniEmpty>
    );
  }

  return (
    <Table isVirtualized={false} headerRowHeight={50} autoHeight data={records} columns={columns} shadow={false} />
  );
};

export default RecordsTab;
